import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Mail, 
  Clock, 
  Check,
  Save,
  ChevronDown,
  ChevronRight,
  Eye,
  Code,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useAdmin } from '@/context/AdminContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEmailSequences() {
  const { token } = useAdmin();
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedSequence, setExpandedSequence] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editMode, setEditMode] = useState('preview'); // 'preview' or 'code'
  
  const [sequenceForm, setSequenceForm] = useState({
    name: '',
    description: '',
    is_active: true,
    is_default: false
  });

  const [templateForm, setTemplateForm] = useState({
    sequence_id: '',
    step_number: 1,
    subject: '',
    html_content: '',
    delay_days: 0
  });

  useEffect(() => {
    fetchSequences();
  }, [token]);

  const fetchSequences = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-sequences?token=${token}`);
      setSequences(response.data);
      
      // Fetch templates for each sequence
      for (const seq of response.data) {
        const templatesRes = await axios.get(`${API}/admin/email-templates/${seq.id}?token=${token}`);
        setTemplates(prev => ({ ...prev, [seq.id]: templatesRes.data }));
      }
    } catch (error) {
      console.error('Error fetching sequences:', error);
      toast.error('Failed to load email sequences');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSequence = async (e) => {
    e.preventDefault();
    try {
      if (editingSequence) {
        await axios.put(`${API}/admin/email-sequences/${editingSequence.id}?token=${token}`, sequenceForm);
        toast.success('Sequence updated');
      } else {
        await axios.post(`${API}/admin/email-sequences?token=${token}`, sequenceForm);
        toast.success('Sequence created');
      }
      fetchSequences();
      resetSequenceForm();
    } catch (error) {
      toast.error('Failed to save sequence');
    }
  };

  const handleDeleteSequence = async (id) => {
    if (!window.confirm('Delete this sequence and all its templates?')) return;
    
    try {
      await axios.delete(`${API}/admin/email-sequences/${id}?token=${token}`);
      setSequences(sequences.filter(s => s.id !== id));
      toast.success('Sequence deleted');
    } catch (error) {
      toast.error('Failed to delete sequence');
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/admin/email-templates/${editingTemplate.id}?token=${token}`, {
          subject: templateForm.subject,
          html_content: templateForm.html_content,
          delay_days: templateForm.delay_days
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/admin/email-templates?token=${token}`, templateForm);
        toast.success('Template created');
      }
      fetchSequences();
      resetTemplateForm();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this email template?')) return;
    
    try {
      await axios.delete(`${API}/admin/email-templates/${id}?token=${token}`);
      fetchSequences();
      toast.success('Template deleted');
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const resetSequenceForm = () => {
    setShowSequenceModal(false);
    setEditingSequence(null);
    setSequenceForm({ name: '', description: '', is_active: true, is_default: false });
  };

  const resetTemplateForm = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateForm({ sequence_id: '', step_number: 1, subject: '', html_content: '', delay_days: 0 });
  };

  const editSequence = (seq) => {
    setEditingSequence(seq);
    setSequenceForm({
      name: seq.name,
      description: seq.description,
      is_active: seq.is_active,
      is_default: seq.is_default
    });
    setShowSequenceModal(true);
  };

  const addTemplate = (sequenceId) => {
    const existingTemplates = templates[sequenceId] || [];
    setTemplateForm({
      sequence_id: sequenceId,
      step_number: existingTemplates.length + 1,
      subject: '',
      html_content: '<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">\n  <h2>Hello {name},</h2>\n  <p>Your message here...</p>\n</div>',
      delay_days: existingTemplates.length === 0 ? 0 : 1
    });
    setShowTemplateModal(true);
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      sequence_id: template.sequence_id,
      step_number: template.step_number,
      subject: template.subject,
      html_content: template.html_content,
      delay_days: template.delay_days
    });
    setShowTemplateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="admin-email-title">Email Sequences</h1>
          <p className="text-slate-400">Manage automated email sequences for new contacts</p>
        </div>
        <Button 
          onClick={() => setShowSequenceModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-sequence-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Sequence
        </Button>
      </div>

      {/* Sequences List */}
      {sequences.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No email sequences yet</p>
            <p className="text-slate-500 text-sm mt-1">Create a sequence to automate follow-up emails</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sequences.map((seq, index) => (
            <motion.div
              key={seq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="bg-slate-800 border-slate-700" data-testid={`sequence-card-${index}`}>
                <Collapsible 
                  open={expandedSequence === seq.id}
                  onOpenChange={() => setExpandedSequence(expandedSequence === seq.id ? null : seq.id)}
                >
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="flex flex-row items-center justify-between p-4">
                      <div className="flex items-center gap-4">
                        {expandedSequence === seq.id ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-white">{seq.name}</h3>
                            {seq.is_default && (
                              <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">
                                Default
                              </span>
                            )}
                            {!seq.is_active && (
                              <span className="bg-slate-600 text-slate-400 text-xs px-2 py-0.5 rounded">
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-slate-400 text-sm">{seq.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-slate-500 text-sm">
                          {templates[seq.id]?.length || 0} emails
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-600 text-slate-400 hover:bg-slate-700"
                          onClick={() => editSequence(seq)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                          onClick={() => handleDeleteSequence(seq.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4">
                      <div className="border-t border-slate-700 pt-4 space-y-3">
                        {(templates[seq.id] || []).map((template, tIndex) => (
                          <div 
                            key={template.id}
                            className="bg-slate-700/50 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                  {template.step_number}
                                </div>
                                <div>
                                  <p className="text-white text-sm font-medium">{template.subject}</p>
                                  <p className="text-slate-500 text-xs flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {template.delay_days === 0 ? 'Sent immediately' : `Sent after ${template.delay_days} day${template.delay_days > 1 ? 's' : ''}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-blue-600/50 text-blue-400 hover:bg-blue-600/20"
                                  onClick={() => setPreviewTemplate(template)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Preview
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-slate-600 text-slate-400 hover:bg-slate-600"
                                  onClick={() => editTemplate(template)}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-600/50 text-red-400 hover:bg-red-600/20"
                                  onClick={() => handleDeleteTemplate(template.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <Button
                          variant="outline"
                          className="w-full border-dashed border-slate-600 text-slate-400 hover:bg-slate-700"
                          onClick={() => addTemplate(seq.id)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Email Step
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Sequence Modal */}
      <Dialog open={showSequenceModal} onOpenChange={resetSequenceForm}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingSequence ? 'Edit Sequence' : 'New Sequence'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveSequence} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input
                value={sequenceForm.name}
                onChange={(e) => setSequenceForm({ ...sequenceForm, name: e.target.value })}
                placeholder="e.g., Welcome Sequence"
                className="bg-slate-700 border-slate-600"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={sequenceForm.description}
                onChange={(e) => setSequenceForm({ ...sequenceForm, description: e.target.value })}
                placeholder="Describe this sequence..."
                className="bg-slate-700 border-slate-600"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Active</Label>
              <Switch
                checked={sequenceForm.is_active}
                onCheckedChange={(checked) => setSequenceForm({ ...sequenceForm, is_active: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Set as Default</Label>
              <Switch
                checked={sequenceForm.is_default}
                onCheckedChange={(checked) => setSequenceForm({ ...sequenceForm, is_default: checked })}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1 border-slate-600" onClick={resetSequenceForm}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={resetTemplateForm}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Email Template' : 'New Email Template'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveTemplate} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Step Number</Label>
                <Input
                  type="number"
                  min="1"
                  value={templateForm.step_number}
                  onChange={(e) => setTemplateForm({ ...templateForm, step_number: parseInt(e.target.value) })}
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Delay (days after signup)</Label>
                <Input
                  type="number"
                  min="0"
                  value={templateForm.delay_days}
                  onChange={(e) => setTemplateForm({ ...templateForm, delay_days: parseInt(e.target.value) })}
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Subject Line</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Email subject..."
                  className="bg-slate-700 border-slate-600"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Email Content</Label>
                <div className="flex gap-1 bg-slate-900 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setEditMode('preview')}
                    className={`px-3 py-1 text-xs rounded flex items-center gap-1 transition-colors ${
                      editMode === 'preview' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Eye className="w-3 h-3" />
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditMode('code')}
                    className={`px-3 py-1 text-xs rounded flex items-center gap-1 transition-colors ${
                      editMode === 'code' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Code className="w-3 h-3" />
                    HTML Code
                  </button>
                </div>
              </div>
              <p className="text-slate-500 text-xs">Use {'{name}'} to insert the contact's name</p>
              
              {editMode === 'code' ? (
                <Textarea
                  value={templateForm.html_content}
                  onChange={(e) => setTemplateForm({ ...templateForm, html_content: e.target.value })}
                  placeholder="<div>Your HTML content...</div>"
                  className="bg-slate-700 border-slate-600 font-mono text-sm min-h-[350px]"
                  required
                />
              ) : (
                <div className="bg-white rounded-lg overflow-hidden min-h-[350px] max-h-[400px] overflow-y-auto">
                  <div 
                    className="p-4"
                    dangerouslySetInnerHTML={{ 
                      __html: templateForm.html_content.replace(/\{name\}/g, '<span style="background:#fef3c7;padding:2px 6px;border-radius:4px;color:#92400e;">John Doe</span>') 
                    }} 
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1 border-slate-600" onClick={resetTemplateForm}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-hidden p-0">
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Email Preview</h3>
                <p className="text-slate-400 text-sm">Step {previewTemplate?.step_number}: {previewTemplate?.subject}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {previewTemplate?.delay_days === 0 ? 'Sent immediately' : `Sent after ${previewTemplate?.delay_days} days`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-400 hover:bg-slate-700"
                  onClick={() => {
                    editTemplate(previewTemplate);
                    setPreviewTemplate(null);
                  }}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </div>
            </div>
          </div>
          
          {/* Email Preview */}
          <div className="bg-slate-600 p-4">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden max-h-[60vh] overflow-y-auto">
              {/* Email Header Mock */}
              <div className="bg-slate-100 px-4 py-3 border-b">
                <p className="text-slate-600 text-sm"><strong>From:</strong> Cornerstone Refund &lt;noreply@cornerstonerefund.com&gt;</p>
                <p className="text-slate-600 text-sm"><strong>To:</strong> john.doe@example.com</p>
                <p className="text-slate-600 text-sm"><strong>Subject:</strong> {previewTemplate?.subject}</p>
              </div>
              {/* Email Body */}
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: previewTemplate?.html_content?.replace(/\{name\}/g, 'John Doe') || '' 
                }} 
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
